import {
    useDropdownItemStyles,
    useDropdownItemTextStyles,
} from "@design-system/Inputs/UniversalDropdown";
import { Box } from "@design-system/Layout/Box";
import { FlexCol } from "@design-system/Layout/Flex";
import { Ellipsis } from "@design-system/Typography/Ellipsis";
import { Text } from "@design-system/Typography/Text";
import { cn } from "@design-system/Utilities";
import { KnowledgeBaseItemType } from "@generated/client/graphql";
import { useMemo, type FC } from "react";
import { useKnowledgeBasePageTranslations } from "../../i18n";
const SidebarItem: FC<{
    active: boolean;
    id: string;
    name: string;
    onClick: (id: string) => void;
}> = ({ active, id, name, onClick }) => {
    const itemClassName = useDropdownItemStyles({ active });
    const textClassName = useDropdownItemTextStyles({
        active,
    });
    const handleClick = () => {
        onClick(id);
    };
    return (
        <Box
            className={cn(
                itemClassName,
                active && "bg-tertiary",
                "min-w-[232px]",
            )}
            onClick={handleClick}
        >
            <Ellipsis
                asChild
                className="min-w-[226px] max-w-[226px]"
                hasTooltip
            >
                <Text
                    className={cn(textClassName, active && "text-brand_hover")}
                >
                    {name}
                </Text>
            </Ellipsis>
        </Box>
    );
};

export const SideBar: FC<{
    activeType: KnowledgeBaseItemType | undefined;
    setActiveType: (type: KnowledgeBaseItemType) => void;
}> = ({ activeType, setActiveType }) => {
    const { t } = useKnowledgeBasePageTranslations();

    const handleClick = (type: KnowledgeBaseItemType) => {
        return () => {
            setActiveType(type);
        };
    };

    const getOrder = (type: KnowledgeBaseItemType) => {
        switch (type) {
            case KnowledgeBaseItemType.CustomItem:
                return 0;
            case KnowledgeBaseItemType.Document:
                return 1;
            case KnowledgeBaseItemType.Questionnaire:
                return 2;
            case KnowledgeBaseItemType.Indicator:
                return 3;
            case KnowledgeBaseItemType.Objective:
                return 4;
            case KnowledgeBaseItemType.Framework:
                return 5;
            default:
                return Object.values(KnowledgeBaseItemType).length;
        }
    };

    const tabs = Object.values(KnowledgeBaseItemType).map((type) => ({
        type,
        order: getOrder(type),
    }));

    const tabsToDisplay = useMemo(
        () =>
            tabs
                .sort((a, b) => a.order - b.order)
                .filter((type) => type.type !== KnowledgeBaseItemType.Unknown),
        [tabs],
    );
    return (
        <FlexCol>
            {tabsToDisplay.map(({ type }) => (
                <SidebarItem
                    active={activeType === type}
                    id={type}
                    name={t(type)}
                    key={type}
                    onClick={handleClick(type)}
                />
            ))}
        </FlexCol>
    );
};
